import { useState, memo, useContext } from 'react';
import {
  Dialog,
  Fab,
  Tooltip,
  tooltipClasses,
  Box,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FileUploadSharp, Attachment, VideoFile, Close } from '@mui/icons-material';
import { ToastContainer } from 'react-toastify';
import './VideoUpload.css';
import 'react-toastify/dist/ReactToastify.css';
import AuthContext from '../../contexts/AuthContext';
import UploadVideo from './UploadVideo';
import UploadDocument from './UploadDocument';

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    fontWeight: 'bold',
    padding: '10px',
  },
}));

const VideoUpload = ({ user, typeOfUser }) => {
  const { openVideoUpload, setOpenVideoUpload } = useContext(AuthContext);
  const [selectedTab, setSelectedTab] = useState(0);

  const handleOpen = () => setOpenVideoUpload(true);

  const handleClose = () => {
    setOpenVideoUpload(false);
    setSelectedTab(0);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <Box>
      <BootstrapTooltip title='Upload Media' placement='left'>
        <Fab
          size='large'
          className='videoUploadButton'
          onClick={handleOpen}
          color='primary'
        >
          <FileUploadSharp />
        </Fab>
      </BootstrapTooltip>

      <Dialog
        open={openVideoUpload}
        onClose={() => {}} // Prevent closing by clicking outside
        className='videoUploadDialog'
        maxWidth='md'
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <AppBar
          sx={{
            position: 'relative',
            background: 'linear-gradient(135deg, #9291f0 0%, #7c7be8 100%)',
            boxShadow: '0 4px 20px rgba(146, 145, 240, 0.3)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
            <Typography
              variant='h5'
              sx={{ 
                fontWeight: 600, 
                fontFamily: 'Lato',
                color: 'white',
                fontSize: '1.5rem',
                whiteSpace: 'nowrap'
              }}
            >
              Upload Media
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Close />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 0, flexShrink: 0 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                mb: 3,
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  minHeight: 56,
                  borderRadius: '16px',
                  margin: '0 4px',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent',
                  backgroundColor: 'rgba(146, 145, 240, 0.05)',
                  '&.Mui-selected': {
                    backgroundColor: '#9291f0',
                    color: 'white',
                    border: '2px solid #9291f0',
                    boxShadow: '0 4px 12px rgba(146, 145, 240, 0.3)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(146, 145, 240, 0.1)',
                    border: '2px solid rgba(146, 145, 240, 0.3)',
                  },
                },
              }}
            >
              <Tab 
                label='Upload Video' 
                icon={<VideoFile />} 
                iconPosition='end'
                sx={{
                  '& .MuiTab-iconWrapper': {
                    color: selectedTab === 0 ? '#9291f0' : 'inherit',
                  },
                }}
              />
              <Tab
                label='Upload Attachments'
                iconPosition='end'
                icon={<Attachment />}
                sx={{
                  '& .MuiTab-iconWrapper': {
                    color: selectedTab === 1 ? '#9291f0' : 'inherit',
                  },
                }}
              />
            </Tabs>
          </Box>

          <Box 
            sx={{ 
              flex: 1,
              overflow: 'auto',
              px: 3,
              pb: 3
            }}
          >
            {selectedTab === 0 && (
              <UploadVideo user={user} typeOfUser={typeOfUser} />
            )}
            {selectedTab === 1 && (
              <UploadDocument user={user} typeOfUser={typeOfUser} />
            )}
          </Box>
        </Box>
      </Dialog>

      <ToastContainer />
    </Box>
  );
};

export default memo(VideoUpload);
